import { isBrand, type Brand } from './brand';

/**
 * Brand state outside the React tree (mirrors the app's brandStore): the
 * post-login branding fetch publishes here and ThemeProvider subscribes.
 * The last-known-good brand is persisted so a reload paints the right identity
 * before `/portal/config` answers.
 */

const STORAGE_KEY = 'elenthor.portal.brand.v1';

let brand: Brand | null = null;
let hydrated = false;
const listeners = new Set<() => void>();

function notify() {
  for (const listener of listeners) listener();
}

export function getBrand(): Brand | null {
  if (!hydrated) hydrateBrandStore();
  return brand;
}

export function subscribeBrand(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Applies a brand, or clears it when the tenant has none. Only a successful response may call this. */
export function setBrand(next: Brand | null) {
  if (JSON.stringify(brand) === JSON.stringify(next)) return;
  brand = next;
  notify();
  try {
    if (next) localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Persistence is a convenience; the brand still applies this session.
  }
}

export function hydrateBrandStore() {
  hydrated = true;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return;
    const parsed: unknown = JSON.parse(stored);
    if (isBrand(parsed)) brand = parsed;
  } catch {
    // Corrupt cache entry: stay on defaults.
  }
}

/**
 * A brand pinned to this deployment, when any — the web equivalent of a
 * white-label build's pinned brandId. `VITE_BRAND_ID` at build time, or a
 * `brandId` query parameter (useful for previews). Custom-domain resolution
 * (workplan A14) will supply this from the hostname later.
 */
export function getPinnedBrandId(): string | null {
  const fromEnv = (import.meta.env.VITE_BRAND_ID as string | undefined)?.trim();
  if (fromEnv) return fromEnv;
  try {
    const fromQuery = new URLSearchParams(window.location.search).get('brandId')?.trim();
    if (fromQuery) return fromQuery;
  } catch {
    /* no window */
  }
  return null;
}
